import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function TermsOfUse() {
  usePageMeta(
    "Terms of Use — Shattara AI",
    "Read the Shattara AI terms of use. Understand your rights and responsibilities when using our AI-powered learning platform."
  );
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navigation />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="container mx-auto px-6 py-16 max-w-4xl">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms & Conditions</h1>
            <p className="text-muted-foreground">Last Updated: January 16, 2025</p>
            <p className="text-muted-foreground">Effective Date: January 16, 2025</p>
          </div>

          <p className="text-foreground/80 leading-relaxed mb-12">
            These Terms & Conditions ("Terms") constitute a legally binding agreement between you ("User", "you") 
            and <strong>Shattara AI for Information Technology</strong> ("Shattara AI", "Company", "we", "us" or "our"), 
            governing your access to and use of the Shattara AI platform, website, mobile applications, and related 
            AI-powered study tools and services (collectively, the "Services").
          </p>

          <p className="text-foreground/80 leading-relaxed mb-12">
            By accessing or using the Services, you agree to be bound by these Terms. If you do not agree, 
            you must stop using the Services.
          </p>

          {/* Summary Box */}
          <div className="bg-primary/5 border-l-4 border-primary rounded-lg p-6 mb-12">
            <h2 className="text-xl font-semibold mb-4">Key Points (Summary)</h2>
            <ul className="space-y-2 text-foreground/80">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>You must be 18+, or 13–17 with guardian consent</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>Do not use Shattara AI for cheating or plagiarism</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>Uploaded content remains yours; we only process it</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>Subscriptions auto-renew (USD + SAR display)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>7-day refund for first subscription only</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-1">•</span>
                <span>AI may be inaccurate — verify before using</span>
              </li>
            </ul>
          </div>

          {/* Table of Contents */}
          <div className="bg-muted/50 rounded-lg p-6 mb-12">
            <h2 className="text-xl font-semibold mb-4">Table of Contents</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                { id: "eligibility", title: "1. Eligibility" },
                { id: "services", title: "2. Description of Services" },
                { id: "accounts", title: "3. Accounts & Security" },
                { id: "content", title: "4. User Content & License" },
                { id: "integrity", title: "5. Academic Integrity" },
                { id: "conduct", title: "6. Prohibited Conduct" },
                { id: "fees", title: "7. Subscription Fees & Payment" },
                { id: "refund", title: "8. Refund Policy" },
                { id: "disclaimer", title: "9. AI Output Disclaimer" },
                { id: "ip", title: "10. Intellectual Property" },
                { id: "termination", title: "11. Termination" },
                { id: "liability", title: "12. Limitation of Liability" },
                { id: "law", title: "13. Governing Law" },
                { id: "changes", title: "14. Changes to Terms" },
                { id: "contact", title: "15. Contact" },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-left text-foreground/70 hover:text-primary transition-colors py-1 px-2 rounded hover:bg-primary/5"
                >
                  {item.title}
                </button>
              ))}
            </div>
          </div>

          {/* Desktop: Regular Sections */}
          <div className="hidden md:block space-y-12">
            <section id="eligibility">
              <h2 className="text-2xl font-bold mb-4">1. Eligibility</h2>
              <div className="text-foreground/80 leading-relaxed space-y-4">
                <p>You may use the Services only if:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You are at least <strong>18 years old</strong>, or</li>
                  <li>You are between <strong>13 and 17 years old</strong> and have the consent of a parent or legal guardian.</li>
                </ul>
                <p>By using the Services, you represent and warrant that you meet these requirements.</p>
              </div>
            </section>

            <section id="services">
              <h2 className="text-2xl font-bold mb-4">2. Description of Services</h2>
              <div className="text-foreground/80 leading-relaxed space-y-4">
                <p>Shattara AI is an AI-powered tutor platform that helps students study more effectively through features that may include:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Upload</strong>: processing of documents (PDF, DOC, images, videos, etc.)</li>
                  <li><strong>Paste</strong>: importing text or online content (articles, YouTube links, webpages, etc.)</li>
                  <li><strong>Record</strong>: transcribing lectures and audio using AI speech technology</li>
                  <li><strong>AI Tutor Chat</strong></li>
                  <li><strong>Flashcards, Quizzes, Summaries & Practice Exams</strong></li>
                  <li><strong>Study Performance Insights & Analytics</strong></li>
                </ul>
                <p>We may update, add, or remove features at any time.</p>
              </div>
            </section>

            <section id="accounts">
              <h2 className="text-2xl font-bold mb-4">3. Accounts & Security</h2>
              <div className="text-foreground/80 leading-relaxed space-y-4">
                <p>To access certain features, you must create an account and provide accurate information. You are responsible for:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Maintaining the confidentiality of your login credentials</li>
                  <li>All activity under your account</li>
                  <li>Immediately notifying us of any unauthorized access</li>
                </ul>
                <p>We may suspend or terminate accounts that violate these Terms.</p>
              </div>
            </section>

            <section id="content">
              <h2 className="text-2xl font-bold mb-4">4. User Content & License</h2>
              <div className="text-foreground/80 leading-relaxed space-y-4">
                <p>"User Content" includes content you upload, paste, record, or otherwise submit.</p>
                <p>You retain ownership of your User Content. However, by using the Services, you grant Shattara AI a <strong>non-exclusive, worldwide, royalty-free license</strong> to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Process, store, transcribe, analyze, and transform your content using AI</li>
                  <li>Generate study materials (flashcards, quizzes, summaries, exams, chats, etc.)</li>
                  <li>Improve our AI models and educational functionality (in de-identified or aggregated form)</li>
                </ul>
                <p>We do <strong>not</strong> claim ownership of your original documents.</p>
              </div>
            </section>

            <section id="integrity">
              <h2 className="text-2xl font-bold mb-4">5. Academic Integrity</h2>
              <div className="text-foreground/80 leading-relaxed space-y-4">
                <p>You agree <strong>not</strong> to use Shattara AI to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Cheat on school, university, standardized, or professional exams</li>
                  <li>Submit AI-generated content as original work when prohibited</li>
                  <li>Violate academic honor codes or plagiarism rules</li>
                  <li>Bypass institutional integrity systems</li>
                </ul>
                <p>Shattara AI is intended to <strong>support learning</strong>, not replace it.</p>
                <p>We may suspend or terminate accounts used for academic misconduct.</p>
              </div>
            </section>

            <section id="conduct">
              <h2 className="text-2xl font-bold mb-4">6. Prohibited Conduct</h2>
              <div className="text-foreground/80 leading-relaxed space-y-4">
                <p>You must <strong>not</strong>:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Copy, scrape, or reverse-engineer any part of the Services</li>
                  <li>Use the Services for commercial resale without permission</li>
                  <li>Upload malicious code, automation, or spam</li>
                  <li>Engage in threatening, abusive, or unlawful behavior</li>
                  <li>Use the platform to create disinformation or harmful content</li>
                </ul>
              </div>
            </section>

            <section id="fees">
              <h2 className="text-2xl font-bold mb-4">7. Subscription Fees & Payment Terms</h2>
              <div className="text-foreground/80 leading-relaxed space-y-4">
                <p>Some features require a paid subscription. Pricing may be displayed in:</p>
                <div className="bg-muted/50 rounded-lg p-4 my-4">
                  <p><strong>USD</strong> and/or <strong>Saudi Riyal (SAR)</strong></p>
                  <p className="text-sm text-muted-foreground">e.g., "USD $20/month (~SAR 75)"</p>
                </div>
                <p>Fees are charged automatically on a <strong>recurring subscription basis</strong> until canceled.</p>
                <h3 className="text-xl font-semibold mt-6 mb-2">Taxes & Currency Conversion</h3>
                <p>Applicable taxes (including VAT in Saudi Arabia, if any) and exchange fees may apply.</p>
              </div>
            </section>

            <section id="refund">
              <h2 className="text-2xl font-bold mb-4">8. Refund Policy</h2>
              <div className="text-foreground/80 leading-relaxed space-y-4">
                <p>We offer a <strong>7-day refund</strong> for <strong>first-time subscription purchases only</strong>.</p>
                <p>Refund requests after the initial 7 days are not eligible unless required by Saudi law.</p>
              </div>
            </section>

            <section id="disclaimer">
              <h2 className="text-2xl font-bold mb-4">9. AI Output Disclaimer</h2>
              <div className="text-foreground/80 leading-relaxed space-y-4">
                <p>The Services use artificial intelligence. As a result, AI-generated content may be:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Inaccurate</li>
                  <li>Incomplete</li>
                  <li>Outdated</li>
                  <li>Biased, or otherwise flawed</li>
                </ul>
                <p>You are responsible for verifying information. We are <strong>not liable</strong> for exam results, academic outcomes, or decisions made based on AI output.</p>
              </div>
            </section>

            <section id="ip">
              <h2 className="text-2xl font-bold mb-4">10. Intellectual Property Rights</h2>
              <div className="text-foreground/80 leading-relaxed space-y-4">
                <p>All platform technology, design, code, trademarks, and other materials are owned by or licensed to Shattara AI. You may not reproduce or exploit them without permission.</p>
              </div>
            </section>

            <section id="termination">
              <h2 className="text-2xl font-bold mb-4">11. Termination</h2>
              <div className="text-foreground/80 leading-relaxed space-y-4">
                <p>We may suspend or terminate your access if:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You violate these Terms</li>
                  <li>Required by law or government request</li>
                  <li>The Service is discontinued</li>
                </ul>
                <p>You may stop using the Service at any time.</p>
              </div>
            </section>

            <section id="liability">
              <h2 className="text-2xl font-bold mb-4">12. Limitation of Liability</h2>
              <div className="text-foreground/80 leading-relaxed space-y-4">
                <p>To the maximum extent permitted by Saudi law:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>The Services are provided <strong>"as is" and "as available"</strong></li>
                  <li>We make <strong>no guarantees</strong> regarding performance, accuracy, or educational outcomes</li>
                  <li>We are <strong>not liable</strong> for indirect, incidental, or consequential damages</li>
                </ul>
              </div>
            </section>

            <section id="law">
              <h2 className="text-2xl font-bold mb-4">13. Governing Law & Dispute Resolution</h2>
              <div className="text-foreground/80 leading-relaxed space-y-4">
                <p>These Terms are governed by the laws and regulations of the <strong>Kingdom of Saudi Arabia</strong>.</p>
                <p>Disputes shall be submitted to the <strong>competent courts in Saudi Arabia</strong>.</p>
              </div>
            </section>

            <section id="changes">
              <h2 className="text-2xl font-bold mb-4">14. Changes to Terms</h2>
              <div className="text-foreground/80 leading-relaxed space-y-4">
                <p>We may amend these Terms at any time. Continued use of the Services constitutes acceptance of the updated Terms.</p>
              </div>
            </section>

            <section id="contact">
              <h2 className="text-2xl font-bold mb-4">15. Contact Information</h2>
              <div className="text-foreground/80 leading-relaxed space-y-4">
                <p>For questions, complaints, or legal notices:</p>
                <div className="bg-muted/50 rounded-lg p-4 mt-4">
                  <p className="font-semibold">Shattara AI for Information Technology</p>
                  <p>Email: hello@shattaraai.com</p>
                  <p>Address: Madinah Road, Tahlia district - 23331 Jeddah, Saudi Arabia</p>
                </div>
              </div>
            </section>
          </div>

          {/* Mobile: Accordion */}
          <div className="md:hidden">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="eligibility">
                <AccordionTrigger>1. Eligibility</AccordionTrigger>
                <AccordionContent>
                  <div className="text-foreground/80 leading-relaxed space-y-4">
                    <p>You may use the Services only if:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>You are at least <strong>18 years old</strong>, or</li>
                      <li>You are between <strong>13 and 17 years old</strong> and have the consent of a parent or legal guardian.</li>
                    </ul>
                    <p>By using the Services, you represent and warrant that you meet these requirements.</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="services">
                <AccordionTrigger>2. Description of Services</AccordionTrigger>
                <AccordionContent>
                  <div className="text-foreground/80 leading-relaxed space-y-4">
                    <p>Shattara AI is an AI-powered tutor platform that helps students study more effectively through features that may include:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Upload</strong>: processing of documents (PDF, DOC, images, videos, etc.)</li>
                      <li><strong>Paste</strong>: importing text or online content (articles, YouTube links, webpages, etc.)</li>
                      <li><strong>Record</strong>: transcribing lectures and audio using AI speech technology</li>
                      <li><strong>AI Tutor Chat</strong></li>
                      <li><strong>Flashcards, Quizzes, Summaries & Practice Exams</strong></li>
                      <li><strong>Study Performance Insights & Analytics</strong></li>
                    </ul>
                    <p>We may update, add, or remove features at any time.</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="accounts">
                <AccordionTrigger>3. Accounts & Security</AccordionTrigger>
                <AccordionContent>
                  <div className="text-foreground/80 leading-relaxed space-y-4">
                    <p>To access certain features, you must create an account and provide accurate information. You are responsible for:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Maintaining the confidentiality of your login credentials</li>
                      <li>All activity under your account</li>
                      <li>Immediately notifying us of any unauthorized access</li>
                    </ul>
                    <p>We may suspend or terminate accounts that violate these Terms.</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="content">
                <AccordionTrigger>4. User Content & License</AccordionTrigger>
                <AccordionContent>
                  <div className="text-foreground/80 leading-relaxed space-y-4">
                    <p>"User Content" includes content you upload, paste, record, or otherwise submit.</p>
                    <p>You retain ownership of your User Content. However, by using the Services, you grant Shattara AI a <strong>non-exclusive, worldwide, royalty-free license</strong> to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Process, store, transcribe, analyze, and transform your content using AI</li>
                      <li>Generate study materials (flashcards, quizzes, summaries, exams, chats, etc.)</li>
                      <li>Improve our AI models and educational functionality (in de-identified or aggregated form)</li>
                    </ul>
                    <p>We do <strong>not</strong> claim ownership of your original documents.</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="integrity">
                <AccordionTrigger>5. Academic Integrity</AccordionTrigger>
                <AccordionContent>
                  <div className="text-foreground/80 leading-relaxed space-y-4">
                    <p>You agree <strong>not</strong> to use Shattara AI to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Cheat on school, university, standardized, or professional exams</li>
                      <li>Submit AI-generated content as original work when prohibited</li>
                      <li>Violate academic honor codes or plagiarism rules</li>
                      <li>Bypass institutional integrity systems</li>
                    </ul>
                    <p>Shattara AI is intended to <strong>support learning</strong>, not replace it.</p>
                    <p>We may suspend or terminate accounts used for academic misconduct.</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="conduct">
                <AccordionTrigger>6. Prohibited Conduct</AccordionTrigger>
                <AccordionContent>
                  <div className="text-foreground/80 leading-relaxed space-y-4">
                    <p>You must <strong>not</strong>:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Copy, scrape, or reverse-engineer any part of the Services</li>
                      <li>Use the Services for commercial resale without permission</li>
                      <li>Upload malicious code, automation, or spam</li>
                      <li>Engage in threatening, abusive, or unlawful behavior</li>
                      <li>Use the platform to create disinformation or harmful content</li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="fees">
                <AccordionTrigger>7. Subscription Fees & Payment</AccordionTrigger>
                <AccordionContent>
                  <div className="text-foreground/80 leading-relaxed space-y-4">
                    <p>Some features require a paid subscription. Pricing may be displayed in:</p>
                    <div className="bg-muted/50 rounded-lg p-4 my-4">
                      <p><strong>USD</strong> and/or <strong>Saudi Riyal (SAR)</strong></p>
                      <p className="text-sm text-muted-foreground">e.g., "USD $20/month (~SAR 75)"</p>
                    </div>
                    <p>Fees are charged automatically on a <strong>recurring subscription basis</strong> until canceled.</p>
                    <h3 className="text-xl font-semibold mt-6 mb-2">Taxes & Currency Conversion</h3>
                    <p>Applicable taxes (including VAT in Saudi Arabia, if any) and exchange fees may apply.</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="refund">
                <AccordionTrigger>8. Refund Policy</AccordionTrigger>
                <AccordionContent>
                  <div className="text-foreground/80 leading-relaxed space-y-4">
                    <p>We offer a <strong>7-day refund</strong> for <strong>first-time subscription purchases only</strong>.</p>
                    <p>Refund requests after the initial 7 days are not eligible unless required by Saudi law.</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="disclaimer">
                <AccordionTrigger>9. AI Output Disclaimer</AccordionTrigger>
                <AccordionContent>
                  <div className="text-foreground/80 leading-relaxed space-y-4">
                    <p>The Services use artificial intelligence. As a result, AI-generated content may be:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Inaccurate</li>
                      <li>Incomplete</li>
                      <li>Outdated</li>
                      <li>Biased, or otherwise flawed</li>
                    </ul>
                    <p>You are responsible for verifying information. We are <strong>not liable</strong> for exam results, academic outcomes, or decisions made based on AI output.</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="ip">
                <AccordionTrigger>10. Intellectual Property</AccordionTrigger>
                <AccordionContent>
                  <div className="text-foreground/80 leading-relaxed space-y-4">
                    <p>All platform technology, design, code, trademarks, and other materials are owned by or licensed to Shattara AI. You may not reproduce or exploit them without permission.</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="termination">
                <AccordionTrigger>11. Termination</AccordionTrigger>
                <AccordionContent>
                  <div className="text-foreground/80 leading-relaxed space-y-4">
                    <p>We may suspend or terminate your access if:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>You violate these Terms</li>
                      <li>Required by law or government request</li>
                      <li>The Service is discontinued</li>
                    </ul>
                    <p>You may stop using the Service at any time.</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="liability">
                <AccordionTrigger>12. Limitation of Liability</AccordionTrigger>
                <AccordionContent>
                  <div className="text-foreground/80 leading-relaxed space-y-4">
                    <p>To the maximum extent permitted by Saudi law:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>The Services are provided <strong>"as is" and "as available"</strong></li>
                      <li>We make <strong>no guarantees</strong> regarding performance, accuracy, or educational outcomes</li>
                      <li>We are <strong>not liable</strong> for indirect, incidental, or consequential damages</li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="law">
                <AccordionTrigger>13. Governing Law</AccordionTrigger>
                <AccordionContent>
                  <div className="text-foreground/80 leading-relaxed space-y-4">
                    <p>These Terms are governed by the laws and regulations of the <strong>Kingdom of Saudi Arabia</strong>.</p>
                    <p>Disputes shall be submitted to the <strong>competent courts in Saudi Arabia</strong>.</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="changes">
                <AccordionTrigger>14. Changes to Terms</AccordionTrigger>
                <AccordionContent>
                  <div className="text-foreground/80 leading-relaxed space-y-4">
                    <p>We may amend these Terms at any time. Continued use of the Services constitutes acceptance of the updated Terms.</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="contact">
                <AccordionTrigger>15. Contact Information</AccordionTrigger>
                <AccordionContent>
                  <div className="text-foreground/80 leading-relaxed space-y-4">
                    <p>For questions, complaints, or legal notices:</p>
                    <div className="bg-muted/50 rounded-lg p-4 mt-4">
                      <p className="font-semibold">Shattara AI for Information Technology</p>
                      <p>Email: hello@shattaraai.com</p>
                      <p>Address: Madinah Road, Tahlia district - 23331 Jeddah, Saudi Arabia</p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>
      </main>

      <Footer />

      {/* Back to Top Button */}
      <Button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 rounded-full h-12 w-12 shadow-lg"
        size="icon"
        aria-label="Back to top"
      >
        <ArrowUp className="h-5 w-5" />
      </Button>
    </div>
  );
}
